import { useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RootState } from "../../redux/store";
import OfflineStorage from "../offlineStorage/OfflineStorage";
import { ComponentWithChildren, IPersonBadge } from "../types";
import Utils from "./utils/utils";
import { FormValues as INewBadge } from "../../components/createBadge/utils/types";
import SearchPageUtils from "../../components/search/container/utils/searchPageUtils";
import { MAX_API_CALL_RETRY_COUNT } from "../constants";
import { useGetMetadataForSync } from "common/hooks/useGetMetadataForSync/useGetMetadataForSync";
import PublicApiService from "../../services/PublicApiService";
import BadgeHelper from "../badges/helper/BadgeHelper";
import { useUserAccess } from "../../components/createBadge/hooks/useUserAccess";
import { useCreateRegistration } from "../../components/createBadge/form/hooks/useCreateRegistration/useCreateRegistration";
import EventHelper from "../network/helpers/event/EventHelper";
import { setEventEditionBadges } from "../../redux/eventEdition/slice/eventEditionSlice";
import { badgeSyncRequestStarted, badgeSyncRequestCompleted, badgeSyncSyncFailed } from "../../redux/metadata/slice/metadataSlice";
import cloneDeep from "lodash.clonedeep";
import { v4 as uuid } from "uuid";

export const NetworkSynchronization: ComponentWithChildren = ({ children }) => {
  const dispatch = useDispatch();
  const offline = useSelector((state: RootState) => state.metadata.offline);
  const { getState } = useStore();
  const { getMetadataForSync } = useGetMetadataForSync();
  const { options } = useUserAccess();
  const { createRegistration } = useCreateRegistration();

  const isNewRegistration = async (password: string, idToFind: string) => {
    const newBadges = await OfflineStorage.getNewRegistrations(password);
    if (newBadges && Array.isArray(newBadges)) {
      return newBadges.find((newBadge: INewBadge) => newBadge.id === idToFind);
    }
    return false;
  };

  const updateBadges = async () => {
    const { pass, gbsCode, eventEdition } = getMetadataForSync();
    const fromStore: IPersonBadge[] | null = await OfflineStorage.getDecryptedItem(OfflineStorage.getBadgesKey(), pass);
    if (fromStore && Array.isArray(fromStore)) {
      const badgesToUpdate = fromStore
        .map(badge => {
          return BadgeHelper.getUpdateBadgeDto(badge, eventEdition, options, badge.orderId);
        })
        .filter(async badge => {
          return !isNewRegistration(pass, badge.badgeId);
        });
      if (badgesToUpdate.length > 0) {
        await PublicApiService.updateBadgesBatch(gbsCode, badgesToUpdate);
        await OfflineStorage.removeBadgesFromStorage(
          badgesToUpdate.map(b => b.badgeId),
          pass
        );
      }
    }
  };

  const updateSingleBadge = async (badgeToUpdate: IPersonBadge, pass: string, retryCount = 0) => {
    if (retryCount >= MAX_API_CALL_RETRY_COUNT) {
      return;
    }
    const { gbsCode, eventEdition } = getMetadataForSync();
    try {
      await PublicApiService.updateBadgesBatch(gbsCode, [
        BadgeHelper.getUpdateBadgeDto(badgeToUpdate, eventEdition, options, badgeToUpdate.orderId)
      ]);
    } catch (e) {
      setTimeout(() => updateSingleBadge(badgeToUpdate, pass, retryCount++), 5000);
    }
  };

  const updateBadgeAfterCreation = async (id: string) => {
    const { pass } = getMetadataForSync();
    const badgesFromCache = await OfflineStorage.getDecryptedItem(OfflineStorage.getBadgesKey(), pass);
    if (badgesFromCache && Array.isArray(badgesFromCache) && badgesFromCache.length > 0) {
      const updatedArray = badgesFromCache.filter((bdg: IPersonBadge) => bdg.id === id);
      for (const objToUpdate of updatedArray) {
        await updateSingleBadge(objToUpdate, pass);
      }
    }
  };

  const syncRegistrations = async () => {
    const { pass, location, eventEdition } = getMetadataForSync();
    const registrationsFromStore: INewBadge[] | null = await OfflineStorage.getNewRegistrations(pass);
    const correlationId = uuid();
    if (registrationsFromStore && Array.isArray(registrationsFromStore) && EventHelper.eventEdition) {
      for (const registration of registrationsFromStore) {
        const { personInfo, orgInfo, id, attributes, preferences } = registration;
        const { customerType, badgeType } = personInfo;
        const organisationId = orgInfo.id === "0" ? "" : orgInfo.id!;
        const { sku } = badgeType;
        try {
          const response = await createRegistration(
            {
              badgeLocation: location,
              selectedProductSku: sku,
              badgeNumber: id,
              attributes,
              personInfo,
              visitorType: customerType,
              companyInfo: orgInfo,
              orgId: organisationId,
              eventEditionId: eventEdition.id
            },
            correlationId,
            preferences
          );

          if (response.data.registrationId) {
            await OfflineStorage.removeNewRegFromStorage(id, pass);
            await updateBadgeAfterCreation(id);
          }
        } catch (e) {
          console.error("error: ", e);
        }
      }
    }
  };

  const syncData = () => {
    setTimeout(async () => {
      dispatch(badgeSyncRequestStarted());
      try {
        await syncRegistrations();
        await updateBadges();
        dispatch(badgeSyncRequestCompleted());
      } catch (e) {
        console.error("Error during network sync: ", e);
        dispatch(badgeSyncSyncFailed());
      }
    }, 8000);
  };

  const syncBadgesFromCache = async () => {
    const badgesFromRedux = cloneDeep([...(getState() as RootState).eventEdition.badges]);
    if (!badgesFromRedux) {
      return;
    }

    const { gbsCode, location, pass } = getMetadataForSync();
    if (!gbsCode) {
      return;
    }

    dispatch(badgeSyncRequestStarted());
    try {
      const newRegistrationsFromStore = await OfflineStorage.getNewRegistrations(pass);
      let newBadgeArray: IPersonBadge[] = JSON.parse(JSON.stringify(badgesFromRedux));
      const updatedBadgesFromCache: IPersonBadge[] | null = await OfflineStorage.getDecryptedItem(OfflineStorage.getBadgesKey(), pass);
      if (newRegistrationsFromStore && Array.isArray(newRegistrationsFromStore) && newRegistrationsFromStore.length > 0) {
        for (const registration of newRegistrationsFromStore) {
          const updatedBadge = (updatedBadgesFromCache || []).find(bg => bg.id === registration.id);
          const transformed = updatedBadge || SearchPageUtils.getTransformedInfoFromNewBadge(registration, gbsCode, pass, location);
          if (newBadgeArray.find(bdg => bdg.id === transformed.id)) {
            newBadgeArray = newBadgeArray.map(bdg => (bdg.id === transformed.id ? transformed : bdg));
          } else {
            newBadgeArray.push(transformed);
          }
        }
        dispatch(setEventEditionBadges([...newBadgeArray.filter(badge => Utils.isBadgeValid(badge))]));
      }

      if (updatedBadgesFromCache) {
        syncUpdatedOfflineBadgesWithStore(updatedBadgesFromCache, newBadgeArray);
      }

      dispatch(badgeSyncRequestCompleted());
    } catch (e) {
      dispatch(badgeSyncSyncFailed());
    }
  };

  const syncUpdatedOfflineBadgesWithStore = (updatedBadgesFromCache: IPersonBadge[], newBadgeArray: IPersonBadge[]) => {
    for (const updated of updatedBadgesFromCache) {
      if (Utils.isBadgeValid(updated)) {
        newBadgeArray = newBadgeArray.map(old => (old.id === updated.id ? updated : old));
      }
    }
    dispatch(setEventEditionBadges([...newBadgeArray]));
  };

  useEffect(() => {
    syncBadgesFromCache();
    if (!offline) {
      syncData();
    }

    setTimeout(syncBadgesFromCache, 2000);
  }, [offline]);

  return <>{children}</>;
};
